import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";

export default function PracticeResult() {
  const { state } = useLocation();
  const nav = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ================= MATH FORMAT FUNCTION ================= */
  function formatMath(text) {
    if (!text) return "";

    const superscriptMap = {
      "0": "⁰",
      "1": "¹",
      "2": "²",
      "3": "³",
      "4": "⁴",
      "5": "⁵",
      "6": "⁶",
      "7": "⁷",
      "8": "⁸",
      "9": "⁹",
      "-": "⁻"
    };

    return text.replace(/\^(-?\d+)/g, (match, power) => {
      return power
        .split("")
        .map(char => superscriptMap[char] || char)
        .join("");
    });
  }

  useEffect(() => {
    if (!state?.attemptId) {
      nav("/student/practice-list");
      return;
    }

    api
      .get(`/study/practice-result/${state.attemptId}`)
      .then((res) => {
        setResult(res.data);
      })
      .catch((err) => {
        console.error(err);
        alert("❌ Failed to load result");
      })
      .finally(() => setLoading(false));
  }, [state, nav]);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const apiBase = api.defaults.baseURL.replace(/\/api\/?$/, "");
    return `${apiBase}${path}`;
  };

  if (loading) {
    return <p style={{ padding: 40 }}>⏳ Loading result...</p>;
  }

  if (!result) {
    return null;
  }

  return (
    <div style={pageStyle}>
      <div style={overlayStyle}>
        <div style={contentWrapper}>
          
          {/* HEADER */}
          <div style={headerRow}>
            <img src="/logo.png" alt="EduAt10" style={logoStyle} />
            <button
              onClick={() => nav("/student/study")}
              style={backBtnStyle}
            >
              ← Back
            </button>
          </div>

          <h2>📊 Practice Result.</h2>

          {/* SUMMARY */}
          <div style={summaryCard}>
            <p><strong>Score:</strong> {result.score ?? 0}</p>
            <p><strong>Total Questions:</strong> {result.total_questions ?? 0}</p>
            <p><strong>Percentage:</strong> {result.percentage ?? 0}%</p>
          </div>

          {/* QUESTIONS */}
          {Array.isArray(result.questions) &&
            result.questions.map((q, index) => (
              <div key={index} style={cardStyle}>
                <p style={{ fontWeight: 600 }}>
                  Q{index + 1}.
                </p>

                {/* Question Text */}
                {q.question_text && (
                  <p style={{ marginBottom: 10 }}>
                    {formatMath(q.question_text)}
                  </p>
                )}

                {/* Question Image */}
                {q.question_image && (
                  <img
                    src={getImageUrl(q.question_image)}
                    alt="Question"
                    style={questionImgStyle}
                  />
                )}

                {/* OPTIONS */}
                {["A", "B", "C", "D"].map((opt) => {
                  const optionImage = q.options?.[opt + "_image"];
                  const optionText = q.options?.[opt];

                  const isCorrect = q.correct_answer === opt;
                  const isSelected = q.student_answer === opt;

                  let bgColor = "transparent";

                  if (isSelected && isCorrect) bgColor = "#d1fae5";
                  else if (isSelected && !isCorrect) bgColor = "#fee2e2";
                  else if (!isSelected && isCorrect) bgColor = "#dcfce7";

                  return (
                    <div
                      key={opt}
                      style={{
                        background: bgColor,
                        padding: 10,
                        marginBottom: 6,
                        borderRadius: 8,
                        border: isCorrect
                          ? "1px solid #16a34a"
                          : "1px solid #ddd",
                      }}
                    >
                      <strong>{opt}.</strong>{" "}
                      {optionImage ? (
                        <img
                          src={getImageUrl(optionImage)}
                          alt={opt}
                          style={optionImgStyle}
                        />
                      ) : (
                        formatMath(optionText)
                      )}
                    </div>
                  );
                })}

                {/* RESULT */}
                {q.is_correct !== undefined && (
                  <div style={{ marginTop: 10 }}>
                    <p
                      style={{
                        color: q.is_correct ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {q.is_correct ? "✔ Correct" : "✖ Wrong"}
                    </p>

                    <p>
                      <strong>Correct Answer:</strong> {q.correct_answer}
                    </p>

                    {q.student_answer && (
                      <p>
                        <strong>Your Answer:</strong> {q.student_answer}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}

          <button
            style={finalBackBtn}
            onClick={() => nav("/student/study")}
          >
            🔙 Back to Study
          </button>

        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const pageStyle = {
  minHeight: "100vh",
  backgroundImage: "url('/eduat10-bg.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

const overlayStyle = {
  minHeight: "100vh",
  backgroundColor: "rgba(0,0,0,0.25)",
  padding: 20,
};

const contentWrapper = {
  background: "rgba(255,255,255,0.95)",
  borderRadius: 16,
  padding: 30,
  maxWidth: 900,
  margin: "0 auto",
};

const logoStyle = {
  width: 120,
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
};

const backBtnStyle = {
  padding: "8px 14px",
  background: "#02884e",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

const summaryCard = {
  background: "#f1f5f9",
  padding: 15,
  borderRadius: 10,
  marginBottom: 25,
};

const cardStyle = {
  marginBottom: 25,
  padding: 20,
  border: "1px solid #ddd",
  borderRadius: 12,
  background: "#ffffff",
};

const questionImgStyle = {
  maxWidth: "100%",
  marginBottom: 10,
  borderRadius: 8,
};

const optionImgStyle = {
  maxWidth: 150,
  display: "block",
  marginTop: 6,
  borderRadius: 6,
};

const finalBackBtn = {
  marginTop: 20,
  padding: "10px 18px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};